import { apiUrl } from "@/api/client";

export interface WidgetStarterSuggestion {
  id: string;
  label: string;
  prompt: string;
  enabled?: boolean;
}

export interface WidgetAppearance {
  brand_name: string | null;
  primary_color: string;
  accent_color: string;
  launcher_label: string;
  welcome_message: string;
  tagline: string;
  beta_notice: string;
  logo_url: string | null;
  position: string;
  radius: number;
  starter_suggestions: WidgetStarterSuggestion[];
  thinking_labels: string[];
}

export interface WidgetAuthProvider {
  enabled: boolean;
  label: string;
  scopes: string[];
  require_for_chat: boolean;
  require_for_write_tools: boolean;
}

export interface WidgetAuthConfig {
  enabled: boolean;
  providers: Record<string, WidgetAuthProvider>;
  personal_context_provider: string | null;
}

export interface WidgetAboutLink {
  label: string;
  url: string;
}

export interface WidgetAbout {
  title?: string;
  body?: string;
  links?: WidgetAboutLink[];
}

export interface WidgetSiteNavigation {
  enabled?: boolean;
  auto_navigate?: boolean;
}

export interface WidgetEmbedConfig {
  tenant: { slug: string; name: string };
  chatbot: { slug: string; name: string };
  appearance: WidgetAppearance;
  auth: WidgetAuthConfig;
  endpoints: Record<string, string>;
  embed_config?: {
    enabled?: boolean;
    allowed_domains?: string[];
    require_allowed_domain?: boolean;
    privacy_notice_url?: string | null;
    show_powered_by?: boolean;
    about?: WidgetAbout;
    site_navigation?: WidgetSiteNavigation;
  };
}

export interface WidgetViewer {
  provider: string;
  name: string | null;
  email: string | null;
  avatar_url: string | null;
  connected: boolean;
  // Credential freshness. `valid === false` means the linked credential has
  // expired and write-capable tools will be refused until the user reconnects.
  // Optional for forward-compat with older backends that omit the field.
  valid?: boolean;
  expires_at?: string | null;
}

export async function fetchAuthStatus(
  tenant: string,
  chatbot: string,
): Promise<WidgetViewer | null> {
  try {
    const url = apiUrl(
      `/api/v1/t/${encodeURIComponent(tenant)}/c/${encodeURIComponent(chatbot)}/auth/status`,
    );
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) return null;
    const body = (await r.json()) as { viewer: WidgetViewer | null };
    return body.viewer;
  } catch {
    return null;
  }
}

export async function fetchEmbedConfig(
  tenant: string,
  chatbot: string,
): Promise<WidgetEmbedConfig | null> {
  try {
    const url = apiUrl(
      `/api/v1/t/${encodeURIComponent(tenant)}/c/${encodeURIComponent(chatbot)}/embed-config`,
    );
    const r = await fetch(url, { credentials: "include" });
    if (!r.ok) return null;
    return (await r.json()) as WidgetEmbedConfig;
  } catch {
    return null;
  }
}

/**
 * Apply appearance to the document root as CSS custom properties so
 * widget.css selectors pick them up. Falls back silently if a field
 * is missing — `widget.css` declares defaults on `:root`.
 */
export function applyTheme(appearance: WidgetAppearance): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const primary = sanitizeColor(appearance.primary_color);
  if (primary) {
    root.style.setProperty("--gw-primary", primary);
    root.style.setProperty("--gw-primary-hover", darken(primary, 0.12));
    root.style.setProperty("--gw-primary-ring", hexToRgba(primary, 0.18));
  }
  const accent = sanitizeColor(appearance.accent_color);
  if (accent) {
    root.style.setProperty("--gw-accent", accent);
  }
  if (typeof appearance.radius === "number" && appearance.radius >= 0) {
    root.style.setProperty("--gw-radius", `${appearance.radius}px`);
  }
}

export function headerTitle(
  appearance: WidgetAppearance,
  fallback: string,
): string {
  const name = (appearance.brand_name || "").trim();
  return name || fallback;
}

function sanitizeColor(value: string | null | undefined): string | null {
  if (!value) return null;
  const v = value.trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(v)) return null;
  return v;
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function darken(hex: string, amount: number): string {
  const r = clamp(parseInt(hex.slice(1, 3), 16) * (1 - amount));
  const g = clamp(parseInt(hex.slice(3, 5), 16) * (1 - amount));
  const b = clamp(parseInt(hex.slice(5, 7), 16) * (1 - amount));
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function clamp(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function toHex(n: number): string {
  return n.toString(16).padStart(2, "0");
}
